import React, { useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import PropTypes from "prop-types";
import classes from "./AddSimCardModal.module.scss";
import { validateSimCard } from "../../../../validation/simCardValidation";

const AddSimCardModal = ({
  t,
  show,
  onClose,
  onSave,
  tableType = "simCards",
}) => {
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    sim: "",
    phonenumber: "",
    pin: "",
    pin2: "",
    puk: "",
    puk2: "",
    cardname: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // IMPORTANT: Reset all validation errors at the start
    setValidationErrors({});

    // Clone formData to avoid modifying state directly
    const formDataToSubmit = { ...formData };

    // Validate the form data
    const errors = validateSimCard(formDataToSubmit);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError(t("validation_errors_found"));
      return;
    }

    try {
      const result = await onSave(formDataToSubmit);

      // Check if submission was successful
      if (!result.success) {
        // If we have a field-specific error, highlight that field
        if (result.errorField) {
          setValidationErrors((prev) => ({
            ...prev,
            [result.errorField]: result.errorCode,
          }));
        }

        // Show the error message at the top of the form
        setError(result.error);
        return;
      }

      // If submission was successful, reset the form and close the modal
      setFormData({
        sim: "",
        phonenumber: "",
        pin: "",
        pin2: "",
        puk: "",
        puk2: "",
        cardname: "",
      });
      setValidationErrors({});
      setError("");
      setValidated(false);
      onClose();
    } catch (err) {
      console.error("Error saving sim card:", err);
      setError(t("errorSavingSimCard"));
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className={classes.addSimCardModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("add_sim_card")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            {/* SIM */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="sim">
                <Form.Label>{t("sim")}</Form.Label>
                <Form.Control
                  type="text"
                  name="sim"
                  value={formData.sim}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.sim}
                  className={`${
                    tableType ? `modal-${tableType}-field-sim` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.sim
                    ? t(validationErrors.sim)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Phone Number */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="phonenumber">
                <Form.Label>{t("phoneNumber")}</Form.Label>
                <Form.Control
                  type="text"
                  name="phonenumber"
                  value={formData.phonenumber}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.phonenumber}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.phonenumber
                    ? t(validationErrors.phonenumber)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PIN */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="pin">
                <Form.Label>{t("pin")}</Form.Label>
                <Form.Control
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.pin}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.pin
                    ? t(validationErrors.pin)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PIN2 */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="pin2">
                <Form.Label>{t("pin2")}</Form.Label>
                <Form.Control
                  type="text"
                  name="pin2"
                  value={formData.pin2}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.pin2}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.pin2
                    ? t(validationErrors.pin2)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PUK */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="puk">
                <Form.Label>{t("puk")}</Form.Label>
                <Form.Control
                  type="text"
                  name="puk"
                  value={formData.puk}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.puk}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.puk
                    ? t(validationErrors.puk)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* PUK2 */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="puk2">
                <Form.Label>{t("puk2")}</Form.Label>
                <Form.Control
                  type="text"
                  name="puk2"
                  value={formData.puk2}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.puk2}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.puk2
                    ? t(validationErrors.puk2)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Card Name */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="cardname">
                <Form.Label>{t("card_name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="cardname"
                  value={formData.cardname}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.cardname}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.cardname
                    ? t(validationErrors.cardname)
                    : t("optional_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddSimCardModal.propTypes = {
  t: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  tableType: PropTypes.string,
};

export default AddSimCardModal;
